import { MessageSquareText, Plus } from "lucide-react";
import { useTranslate } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { DeleteIconButton } from "./DeleteIconButton";
import { SectionTitle } from "./SectionTitle";

export interface NoteItem {
  key: string | number;
  text: string;
  /** Already formatted author/date line */
  meta?: string;
}

export interface NotesSectionProps {
  items: NoteItem[];
  onAdd: (text: string) => Promise<void> | void;
  onDelete: (key: NoteItem["key"]) => Promise<void> | void;
}

/** Notes can be added and deleted, never edited. */
export const NotesSection = ({ items, onAdd, onDelete }: NotesSectionProps) => {
  const translate = useTranslate();
  const [isAdding, setIsAdding] = useState(false);
  const [text, setText] = useState("");

  const handleAdd = async () => {
    if (!text.trim()) return;
    await onAdd(text.trim());
    setText("");
    setIsAdding(false);
  };

  return (
    <section className="flex flex-col gap-2">
      <SectionTitle icon={<MessageSquareText />}>
        {translate("crm.operations.notes.title")}
      </SectionTitle>
      {items.map((item) => (
        <div
          key={item.key}
          className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2"
        >
          <div className="min-w-0 flex-1">
            <p className="whitespace-pre-wrap text-sm">{item.text}</p>
            {item.meta && (
              <p className="mt-1 text-xs text-muted-foreground">{item.meta}</p>
            )}
          </div>
          <DeleteIconButton
            label={translate("crm.operations.notes.delete")}
            onConfirm={() => onDelete(item.key)}
          />
        </div>
      ))}
      {isAdding ? (
        <div className="flex flex-col gap-2">
          <Textarea
            autoFocus
            rows={4}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={translate("crm.operations.notes.placeholder")}
            aria-label={translate("crm.operations.notes.add")}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setText("");
                setIsAdding(false);
              }}
            >
              {translate("ra.action.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!text.trim()}
              onClick={handleAdd}
            >
              {translate("ra.action.add")}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => setIsAdding(true)}
        >
          <Plus />
          {translate("crm.operations.notes.add")}
        </Button>
      )}
    </section>
  );
};
